import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyV2Dto,
  GetOneV2Dto,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DeviceDto } from '../../models/tenants/device.dto';

@Injectable({
  providedIn: 'root',
})
export class DevicesApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/devices`;
  private readonly baseAdminUrl: string = `${this.environment.apiEndpoint}/admin/devices`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<DeviceDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<DeviceDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneV2Dto): Observable<DeviceDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  postOne(data: DeviceDto, auth: AuthHeaders): Observable<DeviceDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOne(id: string, data: DeviceDto, auth: AuthHeaders): Observable<DeviceDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<DeviceDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyAsAdmin(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<DeviceDto>> {
    return this.baseApiService.get(this.baseAdminUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<DeviceDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  getOneAsAdmin(id: string, auth: AuthHeaders, params?: GetOneV2Dto): Observable<DeviceDto> {
    return this.baseApiService.get(`${this.baseAdminUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOneAsAdmin(id: string, data: DeviceDto, auth: AuthHeaders): Observable<DeviceDto> {
    return this.baseApiService.patch(`${this.baseAdminUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DeviceDto>) => this.baseApiService.mapResponse(response)),
    );
  }
}
